import {
  differenceInCalendarDays,
  format,
  isSameDay,
  startOfDay,
  subDays,
} from "date-fns";

export const PROCESSING_VOLUME_PRESETS = {
  LAST_24H: "24h",
  LAST_7D: "7d",
  LAST_30D: "30d",
  CUSTOM: "custom",
};

export const getDefaultProcessingDateRange = () =>
  getProcessingPresetDateRange(PROCESSING_VOLUME_PRESETS.LAST_24H);

export const getProcessingPresetDateRange = (preset) => {
  const today = startOfDay(new Date());

  switch (preset) {
    case PROCESSING_VOLUME_PRESETS.LAST_7D:
      return {
        dateFrom: subDays(today, 6),
        dateTo: today,
        preset: PROCESSING_VOLUME_PRESETS.LAST_7D,
      };
    case PROCESSING_VOLUME_PRESETS.LAST_30D:
      return {
        dateFrom: subDays(today, 29),
        dateTo: today,
        preset: PROCESSING_VOLUME_PRESETS.LAST_30D,
      };
    case PROCESSING_VOLUME_PRESETS.LAST_24H:
    default:
      return {
        dateFrom: subDays(today, 1),
        dateTo: today,
        preset: PROCESSING_VOLUME_PRESETS.LAST_24H,
      };
  }
};

export const detectProcessingDatePreset = (dateFrom, dateTo) => {
  if (!dateFrom || !dateTo) return PROCESSING_VOLUME_PRESETS.LAST_24H;

  const today = startOfDay(new Date());
  const from = startOfDay(dateFrom);
  const to = startOfDay(dateTo);

  if (isSameDay(from, subDays(today, 1)) && isSameDay(to, today)) {
    return PROCESSING_VOLUME_PRESETS.LAST_24H;
  }
  if (isSameDay(from, subDays(today, 6)) && isSameDay(to, today)) {
    return PROCESSING_VOLUME_PRESETS.LAST_7D;
  }
  if (isSameDay(from, subDays(today, 29)) && isSameDay(to, today)) {
    return PROCESSING_VOLUME_PRESETS.LAST_30D;
  }

  return PROCESSING_VOLUME_PRESETS.CUSTOM;
};

export const normalizeProcessingDateRange = (dateRange) => {
  const dateFrom = dateRange?.dateFrom ? startOfDay(dateRange.dateFrom) : null;
  const dateTo = dateRange?.dateTo ? startOfDay(dateRange.dateTo) : null;
  const preset =
    dateRange?.preset === PROCESSING_VOLUME_PRESETS.CUSTOM
      ? PROCESSING_VOLUME_PRESETS.CUSTOM
      : detectProcessingDatePreset(dateFrom, dateTo);

  return { dateFrom, dateTo, preset };
};

export const getProcessingRangeDayCount = (dateRange) => {
  const { dateFrom, dateTo, preset } = normalizeProcessingDateRange(dateRange);

  if (!dateFrom || !dateTo) {
    return 1;
  }

  if (preset === PROCESSING_VOLUME_PRESETS.LAST_24H) {
    return 1;
  }

  return Math.max(1, differenceInCalendarDays(dateTo, dateFrom) + 1);
};

export const getProcessingSparklineGranularity = (dateRange) => {
  const dayCount = getProcessingRangeDayCount(dateRange);
  return dayCount <= 2 ? "hourly" : "daily";
};

export const getProcessingVolumeMultiplier = (dateRange) => {
  const { preset } = normalizeProcessingDateRange(dateRange);
  const dayCount = getProcessingRangeDayCount(dateRange);

  if (preset === PROCESSING_VOLUME_PRESETS.LAST_24H) {
    return 1;
  }
  if (preset === PROCESSING_VOLUME_PRESETS.LAST_7D) {
    return 6.85;
  }
  if (preset === PROCESSING_VOLUME_PRESETS.LAST_30D) {
    return 28.4;
  }

  return Math.max(1, dayCount * 0.96);
};

export const getProcessingVolumeLabelKey = (dateRange) => {
  const { preset } = normalizeProcessingDateRange(dateRange);

  switch (preset) {
    case PROCESSING_VOLUME_PRESETS.LAST_7D:
      return "baas.home.dashboard.processingRails.volumePeriod7d";
    case PROCESSING_VOLUME_PRESETS.LAST_30D:
      return "baas.home.dashboard.processingRails.volumePeriod30d";
    case PROCESSING_VOLUME_PRESETS.CUSTOM:
      return "baas.home.dashboard.processingRails.volumePeriodCustom";
    case PROCESSING_VOLUME_PRESETS.LAST_24H:
    default:
      return "baas.home.dashboard.processingRails.volumePeriod24h";
  }
};

/** Maps home date filter → BaaS/MobileService `period` query (`24h`|`7d`|`30d`|`90d`). */
export const getProcessingPeriodQuery = (dateRange) => {
  const { preset } = normalizeProcessingDateRange(dateRange);
  const dayCount = getProcessingRangeDayCount(dateRange);

  switch (preset) {
    case PROCESSING_VOLUME_PRESETS.LAST_7D:
      return "7d";
    case PROCESSING_VOLUME_PRESETS.LAST_30D:
      return "30d";
    case PROCESSING_VOLUME_PRESETS.CUSTOM:
      if (dayCount <= 2) return "24h";
      if (dayCount <= 10) return "7d";
      if (dayCount <= 45) return "30d";
      return "90d";
    case PROCESSING_VOLUME_PRESETS.LAST_24H:
    default:
      return "24h";
  }
};

/** Calendar bounds for pix-metrics. Home filter is dates (incl. the "24h" chip = yesterday→today). */
export const getProcessingDateQuery = (dateRange) => {
  const { dateFrom, dateTo } = normalizeProcessingDateRange(dateRange);
  if (!dateFrom || !dateTo) return {};
  return {
    date_from: format(dateFrom, "yyyy-MM-dd"),
    date_to: format(dateTo, "yyyy-MM-dd"),
  };
};
